import { ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import { useGetManyReference, useGetOne } from "react-admin";
import { FieldValues, useFormContext } from "react-hook-form";
import type { Bus, Car, Motorcycle, Tractor, Trailer, Truck, Vehicle } from "../providers/vehiclesProvider";
import { SelectInput } from "./SelectInput";
import {
  DrivingLicenseClass,
  isForBus,
  isForBusWithTrailer,
  isForCar,
  isForCarWithTrailer,
  isForMotorcycle,
  isForTractor,
  isForTruck,
  isForTruckWithTrailer,
} from "../model/DrivingLicenseClass";
import { DrivingLesson } from "../model/autovioCalendarEvents";
import { Column } from "../misc/Column";
import { Row } from "../misc/Row";
import { CreateDrivingLessonFormValues } from "../dialogs/AddAppointmentDialog/CreateDrivingLessonForm";
import { CreateExamFormValues } from "../dialogs/AddAppointmentDialog/CreateExamForm";
import { EditDrivingLessonFormValues } from "../dialogs/EventDialog/EditDrivingLessonForm";
import { currentUser, grants } from "../backoffice.access_control";
import { useCurrentDrivingSchoolId } from "../hooks/useCurrentDrivingSchoolId";
import { OWN_MOTORCYCLE_PSEUDO_ID } from "../model/VehicleType";

export function VehiclesInput(props: {
  instructorId?: string;
  drivingLicenseClass: DrivingLicenseClass;
  drivingLessonType?: null | "" | DrivingLesson["drivingLessonType"] | "theoretischePruefung";
  bulkEdit?: boolean;
  size?: "small";
  required?: boolean;
}) {
  const { instructorId, drivingLicenseClass, drivingLessonType, bulkEdit, size, required = false } = props;

  if (!drivingLicenseClass || drivingLessonType === "theoretischePruefung") {
    return null;
  }

  if (isForCar(drivingLicenseClass)) {
    return (
      <>
        <_CarInput label="PKW" instructorId={instructorId} bulkEdit={bulkEdit} size={size} required={required} />
        {isForCarWithTrailer(drivingLicenseClass) && (
          <_TrailerInput
            label="Anhänger"
            instructorId={instructorId}
            bulkEdit={bulkEdit}
            size={size}
            required={required}
          />
        )}
      </>
    );
  } else if (isForMotorcycle(drivingLicenseClass)) {
    if (drivingLessonType === "praktischePruefung") {
      return (
        <>
          <_MotorcycleInput
            label="Motorrad für Fahrschüler"
            drivingLicenseClass={drivingLicenseClass}
            instructorId={instructorId}
            bulkEdit={bulkEdit}
            size={size}
            required={required}
          />
          <_CarInput
            label="PKW für Fahrlehrer und Prüfer"
            instructorId={instructorId}
            bulkEdit={bulkEdit}
            size={size}
            required={required}
          />
        </>
      );
    } else {
      return (
        <>
          <_MotorcycleInput
            label="Motorrad für Fahrschüler"
            drivingLicenseClass={drivingLicenseClass}
            instructorId={instructorId}
            bulkEdit={bulkEdit}
            size={size}
            required={required}
          />
          <_SecondCarOrMotorcycleInput
            label="Begleitfahrzeug für Fahrlehrer"
            instructorId={instructorId}
            bulkEdit={bulkEdit}
            size={size}
            required={required}
          />
        </>
      );
    }
  } else if (isForTruck(drivingLicenseClass)) {
    return (
      <>
        <_TruckInput label="LKW" instructorId={instructorId} bulkEdit={bulkEdit} size={size} required={required} />
        {isForTruckWithTrailer(drivingLicenseClass) && (
          <_TrailerInput
            label="Anhänger"
            instructorId={instructorId}
            bulkEdit={bulkEdit}
            size={size}
            required={required}
          />
        )}
      </>
    );
  } else if (isForBus(drivingLicenseClass)) {
    return (
      <>
        <_BusInput label="Bus" instructorId={instructorId} bulkEdit={bulkEdit} size={size} required={required} />
        {isForBusWithTrailer(drivingLicenseClass) && (
          <_TrailerInput
            label="Anhänger"
            instructorId={instructorId}
            bulkEdit={bulkEdit}
            size={size}
            required={required}
          />
        )}
      </>
    );
  } else if (isForTractor(drivingLicenseClass)) {
    return (
      <>
        <_TractorInput
          label="Traktor"
          instructorId={instructorId}
          bulkEdit={bulkEdit}
          size={size}
          required={required}
        />
      </>
    );
  } else {
    throw new Error(`Unsupported driving license class: ${drivingLicenseClass}`);
  }
}

function _MotorcycleInput(props: {
  label: string;
  instructorId?: string;
  required: boolean;
  bulkEdit?: boolean;
  size?: "small";
  drivingLicenseClass: DrivingLicenseClass;
}) {
  const { label, bulkEdit, size, drivingLicenseClass, required } = props;
  const drivingSchoolId = useCurrentDrivingSchoolId();
  const canListAllVehicles = grants.has("DrivingSchool:vehicle:list");
  const instructorId = props.instructorId ?? (canListAllVehicles ? undefined : currentUser!.uid);
  const { data: instructor } = useGetOne("instructors", { id: instructorId }, { enabled: !!instructorId });
  const { data: motorcycles } = useGetManyReference<Motorcycle>("motorcycles", {
    ...(canListAllVehicles
      ? { target: "drivingSchoolId", id: drivingSchoolId }
      : { target: "entitledInstructorIds", id: instructorId }),
    filter: { drivingSchoolId, drivingLicenseClasses: drivingLicenseClass },
    sort: { field: "name", order: "ASC" },
    pagination: { page: 1, perPage: 999 },
  });
  return (
    <SelectInput
      source="motorcycleId"
      label={label}
      {...(canListAllVehicles && instructorId
        ? {
            groups: ["Zugewiesen", "Weitere Fahrzeuge"],
            optionsWithGroups: [[OWN_MOTORCYCLE_PSEUDO_ID, "Eigenes Motorrad" as ReactNode, "Eigenes Fahrzeug"]].concat(
              buildVehicleOptions(motorcycles, instructorId) ?? [],
            ) as Array<[string, ReactNode, string]>,
          }
        : {
            options: [[OWN_MOTORCYCLE_PSEUDO_ID, "Eigenes Motorrad" as ReactNode]].concat(
              buildVehicleOptions(motorcycles) ?? [],
            ) as Array<[string, ReactNode]>,
          })}
      validate={(value) => (!value && required ? "Bitte wähle ein Motorrad aus." : true)}
      bulkEdit={bulkEdit}
      size={size}
      renderValue={(id) => {
        if (id === OWN_MOTORCYCLE_PSEUDO_ID) return "Eigenes Motorrad";
        if (id === "__DO_NOT_CHANGE__") {
          return (
            <Box component="i" sx={{ color: "text.disabled" }}>
              nicht ändern
            </Box>
          );
        }
        const selectedMotorcycle = motorcycles?.find((it) => it.id === id);
        if (!selectedMotorcycle) return id;
        return _renderVehicleOption(selectedMotorcycle);
      }}
      warning={
        motorcycles?.length === 0 &&
        (canListAllVehicles
          ? "Keine Motorräder vorhanden."
          : instructorId === currentUser!.uid
            ? "Dir ist kein Motorrad zugewiesen."
            : `${instructor?.name ?? "Dem gewählten Fahrlehrer"} ist kein Motorrad zugewiesen.`)
      }
    />
  );
}

function _renderMotorcycleOption(motorcycle: Motorcycle) {
  const availableDrivingLicenseClasses = motorcycle.drivingLicenseClasses;
  if (availableDrivingLicenseClasses.length === 0) {
    return _renderVehicleOption(motorcycle);
  }
  return (
    <Column>
      <Row>
        <Typography variant="caption" sx={{ mr: 0.5, fontWeight: "500" }}>
          Klassen:
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {availableDrivingLicenseClasses.map((dlc) => dlc).join(", ")}
        </Typography>
      </Row>
      <Typography variant="body1" sx={{ fontWeight: "400" }}>
        {_renderVehicleOption(motorcycle)}
      </Typography>
    </Column>
  );
}

function _SecondCarOrMotorcycleInput(props: {
  label: string;
  instructorId?: string;
  required: boolean;
  bulkEdit?: boolean;
  size?: "small";
}) {
  const { label, bulkEdit, size, required } = props;
  const drivingSchoolId = useCurrentDrivingSchoolId();
  const canListAllVehicles = grants.has("DrivingSchool:vehicle:list");
  const instructorId = props.instructorId ?? (canListAllVehicles ? undefined : currentUser!.uid);
  const { data: instructor } = useGetOne("instructors", { id: instructorId }, { enabled: !!instructorId });
  const { watch } = useFormContext<FieldValues>();
  const motorcycleId: string = watch("motorcycleId");
  const { data: motorcycles } = useGetManyReference<Motorcycle>("motorcycles", {
    ...(canListAllVehicles
      ? { target: "drivingSchoolId", id: drivingSchoolId }
      : { target: "entitledInstructorIds", id: instructorId }),
    filter: { drivingSchoolId },
    sort: { field: "name", order: "ASC" },
    pagination: { page: 1, perPage: 999 },
  });
  const { data: cars } = useGetManyReference<Car>("cars", {
    ...(canListAllVehicles
      ? { target: "drivingSchoolId", id: drivingSchoolId }
      : { target: "entitledInstructorIds", id: instructorId }),
    filter: { drivingSchoolId },
    sort: { field: "name", order: "ASC" },
    pagination: { page: 1, perPage: 999 },
  });
  const vehicles = motorcycles && cars && [...motorcycles, ...cars];
  return (
    <SelectInput
      source="secondMotorcycleOrCarId"
      label={label}
      {...(canListAllVehicles && instructorId
        ? {
            groups: ["Zugewiesen", "Weitere Fahrzeuge"],
            optionsWithGroups: buildVehicleOptions(vehicles, instructorId),
          }
        : { options: buildVehicleOptions(vehicles) })}
      validate={(value) =>
        !value && required
          ? "Bitte wähle ein Fahrzeug aus."
          : value && value === motorcycleId
            ? "Bitte wähle ein anderes Fahrzeug aus."
            : true
      }
      bulkEdit={bulkEdit}
      size={size}
      warning={
        (vehicles?.length === 0 &&
          (canListAllVehicles
            ? "Keine Fahrzeuge vorhanden."
            : instructorId === currentUser!.uid
              ? "Dir ist kein Fahrzeug zugewiesen."
              : `${instructor?.name ?? "Dem gewählten Fahrlehrer"} ist kein Fahrzeug zugewiesen.`)) ||
        (vehicles?.length === 1 &&
          vehicles[0].id === motorcycleId &&
          (canListAllVehicles
            ? "Keine weiteren Fahrzeuge vorhanden."
            : instructorId === currentUser!.uid
              ? "Dir ist kein weiteres Fahrzeug zugewiesen."
              : `${instructor?.name ?? "Dem gewählten Fahrlehrer"} ist kein weiteres Fahrzeug zugewiesen.`))
      }
    />
  );
}

function _TrailerInput(props: {
  label: string;
  instructorId?: string;
  required: boolean;
  bulkEdit?: boolean;
  size?: "small";
}) {
  const { label, bulkEdit, size, required } = props;
  const drivingSchoolId = useCurrentDrivingSchoolId();
  const canListAllVehicles = grants.has("DrivingSchool:vehicle:list");
  const instructorId = props.instructorId ?? (canListAllVehicles ? undefined : currentUser!.uid);
  const { data: instructor } = useGetOne("instructors", { id: instructorId }, { enabled: !!instructorId });
  const { data: trailers } = useGetManyReference<Trailer>("trailers", {
    ...(canListAllVehicles
      ? { target: "drivingSchoolId", id: drivingSchoolId }
      : { target: "entitledInstructorIds", id: instructorId }),
    filter: { drivingSchoolId },
    sort: { field: "name", order: "ASC" },
    pagination: { page: 1, perPage: 999 },
  });
  return (
    <SelectInput
      source="trailerId"
      label={label}
      {...(canListAllVehicles && instructorId
        ? {
            groups: ["Zugewiesen", "Weitere Fahrzeuge"],
            optionsWithGroups: buildVehicleOptions(trailers, instructorId),
          }
        : { options: buildVehicleOptions(trailers) })}
      validate={(value) => (!value && required ? "Bitte wähle einen Anhänger aus." : true)}
      bulkEdit={bulkEdit}
      size={size}
      warning={
        trailers?.length === 0 &&
        (canListAllVehicles
          ? "Keine Anhänger vorhanden."
          : instructorId === currentUser!.uid
            ? "Dir ist kein Anhänger zugewiesen."
            : `${instructor?.name ?? "Dem gewählten Fahrlehrer"} ist kein Anhänger zugewiesen.`)
      }
    />
  );
}

function _CarInput(props: {
  label: string;
  instructorId?: string;
  required: boolean;
  bulkEdit?: boolean;
  size?: "small";
}) {
  const { label, bulkEdit, size, required } = props;
  const drivingSchoolId = useCurrentDrivingSchoolId();
  const canListAllVehicles = grants.has("DrivingSchool:vehicle:list");
  const instructorId = props.instructorId ?? (canListAllVehicles ? undefined : currentUser!.uid);
  const { data: instructor } = useGetOne("instructors", { id: instructorId }, { enabled: !!instructorId });
  const { data: cars } = useGetManyReference<Car>("cars", {
    ...(canListAllVehicles
      ? { target: "drivingSchoolId", id: drivingSchoolId }
      : { target: "entitledInstructorIds", id: instructorId }),
    filter: { drivingSchoolId },
    sort: { field: "name", order: "ASC" },
    pagination: { page: 1, perPage: 999 },
  });
  const { watch } = useFormContext<
    CreateDrivingLessonFormValues | CreateExamFormValues | EditDrivingLessonFormValues
  >();
  const drivingLessonType = watch("drivingLessonType");
  return (
    <SelectInput
      source="carId"
      label={label}
      {...(canListAllVehicles && instructorId
        ? {
            groups: ["Zugewiesen", "Weitere Fahrzeuge"],
            optionsWithGroups: buildVehicleOptions(cars, instructorId),
          }
        : { options: buildVehicleOptions(cars) })}
      validate={(value) => {
        if (!value && required) {
          return "Bitte wähle einen PKW aus.";
        }
        if (value === "__DO_NOT_CHANGE__") {
          return true;
        }
        if (drivingLessonType === "schaltkompetenz" && required) {
          if (cars?.find((car) => car.id === value)?.gearType !== "manual") {
            return "Bitte wähle ein Schaltwagen aus.";
          }
        }
        return true;
      }}
      bulkEdit={bulkEdit}
      size={size}
      warning={
        cars?.length === 0 &&
        (canListAllVehicles
          ? "Keine PKWs vorhanden."
          : instructorId === currentUser!.uid
            ? "Dir ist kein PKW zugewiesen."
            : `${instructor?.name ?? "Dem gewählten Fahrlehrer"} ist kein PKW zugewiesen.`)
      }
    />
  );
}

function _TruckInput(props: {
  label: string;
  instructorId?: string;
  required: boolean;
  bulkEdit?: boolean;
  size?: "small";
}) {
  const { label, bulkEdit, size, required } = props;
  const drivingSchoolId = useCurrentDrivingSchoolId();
  const canListAllVehicles = grants.has("DrivingSchool:vehicle:list");
  const instructorId = props.instructorId ?? (canListAllVehicles ? undefined : currentUser!.uid);
  const { data: instructor } = useGetOne("instructors", { id: instructorId }, { enabled: !!instructorId });
  const { data: trucks } = useGetManyReference<Truck>("trucks", {
    ...(canListAllVehicles
      ? { target: "drivingSchoolId", id: drivingSchoolId }
      : { target: "entitledInstructorIds", id: instructorId }),
    filter: { drivingSchoolId },
    sort: { field: "name", order: "ASC" },
    pagination: { page: 1, perPage: 999 },
  });
  return (
    <SelectInput
      source="truckId"
      label={label}
      {...(canListAllVehicles && instructorId
        ? {
            groups: ["Zugewiesen", "Weitere LKWs"],
            optionsWithGroups: buildVehicleOptions(trucks, instructorId),
          }
        : { options: buildVehicleOptions(trucks) })}
      validate={(value) => {
        if (!value && required) {
          return "Bitte wähle einen LKW aus.";
        }
        return true;
      }}
      bulkEdit={bulkEdit}
      size={size}
      warning={
        trucks?.length === 0 &&
        (canListAllVehicles
          ? "Keine LKWs vorhanden."
          : instructorId === currentUser!.uid
            ? "Dir ist kein LKW zugewiesen."
            : `${instructor?.name ?? "Dem gewählten Fahrlehrer"} ist kein LKW zugewiesen.`)
      }
    />
  );
}

function _BusInput(props: {
  label: string;
  instructorId?: string;
  required: boolean;
  bulkEdit?: boolean;
  size?: "small";
}) {
  const { label, bulkEdit, size, required } = props;
  const drivingSchoolId = useCurrentDrivingSchoolId();
  const canListAllVehicles = grants.has("DrivingSchool:vehicle:list");
  const instructorId = props.instructorId ?? (canListAllVehicles ? undefined : currentUser!.uid);
  const { data: instructor } = useGetOne("instructors", { id: instructorId }, { enabled: !!instructorId });
  const { data: buses } = useGetManyReference<Bus>("buses", {
    ...(canListAllVehicles
      ? { target: "drivingSchoolId", id: drivingSchoolId }
      : { target: "entitledInstructorIds", id: instructorId }),
    filter: { drivingSchoolId },
    sort: { field: "name", order: "ASC" },
    pagination: { page: 1, perPage: 999 },
  });
  return (
    <SelectInput
      source="busId"
      label={label}
      {...(canListAllVehicles && instructorId
        ? {
            groups: ["Zugewiesen", "Weitere Busse"],
            optionsWithGroups: buildVehicleOptions(buses, instructorId),
          }
        : { options: buildVehicleOptions(buses) })}
      validate={(value) => {
        if (!value && required) {
          return "Bitte wähle einen Bus aus.";
        }
        return true;
      }}
      bulkEdit={bulkEdit}
      size={size}
      warning={
        buses?.length === 0 &&
        (canListAllVehicles
          ? "Keine Busse vorhanden."
          : instructorId === currentUser!.uid
            ? "Dir ist kein Bus zugewiesen."
            : `${instructor?.name ?? "Dem gewählten Fahrlehrer"} ist kein Bus zugewiesen.`)
      }
    />
  );
}

function _TractorInput(props: {
  label: string;
  instructorId?: string;
  required: boolean;
  bulkEdit?: boolean;
  size?: "small";
}) {
  const { label, bulkEdit, size, required } = props;
  const drivingSchoolId = useCurrentDrivingSchoolId();
  const canListAllVehicles = grants.has("DrivingSchool:vehicle:list");
  const instructorId = props.instructorId ?? (canListAllVehicles ? undefined : currentUser!.uid);
  const { data: instructor } = useGetOne("instructors", { id: instructorId }, { enabled: !!instructorId });
  const { data: tractors } = useGetManyReference<Tractor>("tractors", {
    ...(canListAllVehicles
      ? { target: "drivingSchoolId", id: drivingSchoolId }
      : { target: "entitledInstructorIds", id: instructorId }),
    filter: { drivingSchoolId },
    sort: { field: "name", order: "ASC" },
    pagination: { page: 1, perPage: 999 },
  });
  return (
    <SelectInput
      source="tractorId"
      label={label}
      {...(canListAllVehicles && instructorId
        ? {
            groups: ["Zugewiesen", "Weitere Fahrzeuge"],
            optionsWithGroups: buildVehicleOptions(tractors, instructorId),
          }
        : { options: buildVehicleOptions(tractors) })}
      validate={(value) => {
        if (!value && required) {
          return "Bitte wähle einen Traktor aus.";
        }
        return true;
      }}
      bulkEdit={bulkEdit}
      size={size}
      warning={
        tractors?.length === 0 &&
        (canListAllVehicles
          ? "Keine Traktoren vorhanden."
          : instructorId === currentUser!.uid
            ? "Dir ist kein Traktor zugewiesen."
            : `${instructor?.name ?? "Dem gewählten Fahrlehrer"} ist kein Traktor zugewiesen.`)
      }
    />
  );
}

function _renderVehicleOption(vehicle: Vehicle): string {
  const { type, name, plateNumber, gearType } = vehicle;
  if (type === "car" && gearType) {
    return `${name} • ${plateNumber} (${_formatGearType(gearType)})`;
  } else {
    return `${name}${plateNumber ? ` • ${plateNumber}` : ""}`;
  }
}

function _formatGearType(gearType: "manual" | "automatic" | "electric") {
  if (gearType === "manual") {
    return "Schaltwagen";
  } else if (gearType === "automatic") {
    return "Automatik";
  } else if (gearType === "electric") {
    return "Elektrisch";
  } else {
    return gearType;
  }
}

function buildVehicleOptions(
  vehicles: Array<Vehicle> | undefined,
  instructorId: string,
): Array<[string, ReactNode, string]> | undefined;
function buildVehicleOptions(
  vehicles: Array<Vehicle> | undefined,
  instructorId?: undefined,
): Array<[string, ReactNode]> | undefined;
function buildVehicleOptions(
  vehicles: Array<Vehicle> | undefined,
  instructorId?: string,
): Array<[string, ReactNode, string]> | Array<[string, ReactNode]> | undefined {
  if (!vehicles) {
    return undefined;
  }
  if (instructorId) {
    return vehicles.map((vehicle) => [
      vehicle.id.toString(),
      vehicle.type === "motorcycle" ? _renderMotorcycleOption(vehicle) : _renderVehicleOption(vehicle),
      vehicle.entitledInstructorIds.includes(instructorId.toString()) ? "Zugewiesen" : "Weitere Fahrzeuge",
    ]);
  }
  return vehicles.map((vehicle) => [
    vehicle.id.toString(),
    vehicle.type === "motorcycle" ? _renderMotorcycleOption(vehicle) : _renderVehicleOption(vehicle),
  ]);
}
